import type PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";

type PermissionCacheTag = { type: "Permission"; id: string };

/**
 * The narrow tags a permissions read carries beside `Permission LIST`, so that a grant another
 * administrator changed refetches the person, record, kind and home reads it can appear in, and
 * nothing else (issue #6785).
 *
 * The viewer's own mutations still invalidate `LIST`, which every read carries, so they refetch
 * exactly as before. Only `permissions-ably-redux-middleware.ts` invalidates these.
 */
export const permissionCacheTags = {
  /** Every read narrowed to this person: their person page. */
  grantee: (granteeUserId: string): PermissionCacheTag => ({ type: "Permission", id: `GRANTEE:${granteeUserId}` }),
  /** Every read narrowed to this record, and no person: a record page's grant list. */
  target: (targetId: string): PermissionCacheTag => ({ type: "Permission", id: `TARGET:${targetId}` }),
  /** Every read narrowed to this resource type, and no person or record: a kind's page. */
  resourceType: (resourceType: string): PermissionCacheTag => ({
    type: "Permission",
    id: `RESOURCE_TYPE:${resourceType}`,
  }),
  /** A read across the whole organization, which any change can move: the home page and its counts. */
  organization: { type: "Permission", id: "ORGANIZATION" } as PermissionCacheTag,
} as const;

/** The records a read is narrowed to, by whichever filter names them. */
function targetIdsOf(filters: PermissionFilterParameters): string[] {
  return [
    ...(filters.targetIds ?? []),
    ...(filters.documentIds ?? []),
    ...(filters.forumIds ?? []),
    ...(filters.proposalIds ?? []),
    ...(filters.workAgreementIds ?? []),
  ].map((id) => id.value);
}

/** The resource types a read is narrowed to: its `resourceTypes`, or the type its `resource` containment names. */
function resourceTypesOf(filters: PermissionFilterParameters): string[] {
  if (filters.resourceTypes?.length) return filters.resourceTypes;
  const resource = typeof filters.resource === "string" ? parseResource(filters.resource) : filters.resource;
  if (resource && typeof resource === "object" && "resourceType" in resource && typeof resource.resourceType === "string") {
    return [resource.resourceType];
  }
  return [];
}

function parseResource(resource: string): unknown {
  try {
    return JSON.parse(resource);
  } catch {
    return undefined;
  }
}

/**
 * The narrow tags of one read of `GET /permissions`, by its narrowest filter: the people it names,
 * else the records, else the resource types, else the whole organization.
 *
 * Each read carries the tag of every change that can put a row into it or take one out, and as few
 * others as that allows. A read narrowed to a person changes only when that person's grants do,
 * whatever else narrows it; one narrowed to a record only when a grant on that record does; and so
 * on. A read narrowed by nothing these can name — the home page — carries the organization tag,
 * which every change invalidates.
 */
export function permissionQueryTags(filters: PermissionFilterParameters | undefined): PermissionCacheTag[] {
  if (!filters) return [permissionCacheTags.organization];

  const granteeUserIds = filters.granteeUserIds?.map((id) => id.value) ?? [];
  if (granteeUserIds.length) return granteeUserIds.map(permissionCacheTags.grantee);

  const targetIds = targetIdsOf(filters);
  if (targetIds.length) return targetIds.map(permissionCacheTags.target);

  const resourceTypes = resourceTypesOf(filters);
  if (resourceTypes.length) return resourceTypes.map(permissionCacheTags.resourceType);

  return [permissionCacheTags.organization];
}

/** One grant another administrator changed, as the `permissions-changed` event names it. */
export type PermissionChange = Readonly<{ granteeUserId: string; resourceType: string; targetId?: string | null }>;

/**
 * Every tag one changed grant invalidates: its person, its record when it names one, its resource
 * type, and the organization. A read carries one of these exactly when the grant can appear in it.
 */
export function permissionChangeTags(change: PermissionChange): PermissionCacheTag[] {
  return [
    permissionCacheTags.grantee(change.granteeUserId),
    ...(change.targetId ? [permissionCacheTags.target(change.targetId)] : []),
    permissionCacheTags.resourceType(change.resourceType),
    permissionCacheTags.organization,
  ];
}
